import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/utils/axios';
import { getDeepgramAuthorizationHeader } from '../utils/deepgram-config';
import { clearDeepgramAccessTokenCache } from '../services/deepgram-service';

/**
 * Preprocess text to optimize it for Text-to-Speech (TTS) rendering.
 * Handles special characters, punctuation, and edge cases for smoother output.
 *
 * @param {string} text - The input text to preprocess.
 * @returns {string} - The preprocessed text.
 */
function preprocessForTTS(text: string): string {
  if (!text || typeof text !== 'string') {
    throw new Error('Input must be a non-empty string.');
  }

  // Map of special characters to their spoken equivalents
  const charReplacements: Record<string, string> = {
    ':': ',', // Replace colon with "colon"
    '&': 'and', // Replace ampersand with "and"
    '=': 'equals', // Replace equals sign with "equals"
    '/': 'slash', // Replace slash with "slash"
    '%': 'percent', // Replace percent sign with "percent"
    '*': '', // Replace asterisk with "asterisk"
    '\n': ' ',
    '@': 'at', // Replace at sign with "at"
  };

  // Escape unsupported HTML or XML-like characters
  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };

  // Replace special characters with their spoken equivalents
  const replacedText = text.replace(
    // eslint-disable-next-line no-useless-escape
    /[:&=/\%\*@]/g,
    (char) => charReplacements[char] || char
  );

  // Escape HTML or XML-like characters to prevent misinterpretation
  const escapedText = replacedText.replace(
    /[&<>"']/g,
    (char) => htmlEscapeMap[char]
  );

  // Normalize spacing and punctuation for smoother reading
  const normalizedText = escapedText
    .replace(/([.,!?])(?=\S)/g, '$1 ') // Add space after punctuation if missing
    .replace(/\s{2,}/g, ' ') // Replace multiple spaces with a single space
    .trim(); // Remove leading and trailing spaces

  return normalizedText;
}

// Removed chunkText function as backend can handle longer texts

export interface TTSVoiceConfig {
  provider: 'openai' | 'deepgram';
  voice:
    | 'alloy'
    | 'echo'
    | 'fable'
    | 'onyx'
    | 'nova'
    | 'shimmer'
    | 'aura-2-helena-en';
}

export const TTS_VOICES: TTSVoiceConfig[] = [
  { provider: 'openai', voice: 'alloy' },
  { provider: 'openai', voice: 'echo' },
  { provider: 'openai', voice: 'fable' },
  { provider: 'openai', voice: 'onyx' },
  { provider: 'openai', voice: 'nova' },
  { provider: 'openai', voice: 'shimmer' },
  { provider: 'deepgram', voice: 'aura-2-helena-en' },
];

export const useTTS = () => {
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [loadingMessageId, setLoadingMessageId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastPlayedMessageId = useRef<string | null>(null);
  //   const { config } = useConfig(); // Get config from context
  const [currentVoice, setCurrentVoice] = useState<TTSVoiceConfig>(
    TTS_VOICES[0]
  );

  const generateBackendTTS = async (text: string) => {
    const transformedText = preprocessForTTS(text);

    try {
      const response = await api.post(
        '/agentos/tts/generate/',
        {
          text: transformedText,
          voice: currentVoice.voice,
          model: 'tts-1',
          speed: 1.0,
          response_format: 'wav',
        },
        {
          responseType: 'blob', // Important: tell axios to expect binary data
        }
      );

      return response.data; // This will be a Blob
    } catch (error: any) {
      console.error('Backend TTS error:', error);

      // Handle specific error cases
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (typeof errorData === 'string') {
          try {
            const parsedError = JSON.parse(errorData);
            toast.error(
              parsedError.error?.message || 'Text too long or invalid'
            );
          } catch {
            toast.error('Invalid request to TTS service');
          }
        } else {
          toast.error(errorData.error || 'Invalid request to TTS service');
        }
      } else if (error.response?.status === 500) {
        toast.error('TTS service temporarily unavailable');
      } else {
        toast.error('Error processing TTS request. Try again later.');
      }
      throw error;
    }
  };

  const generateDeepgramSpeech = async (text: string) => {
    const transformedText = preprocessForTTS(text);
    const requestSpeech = (authorization: string) =>
      fetch(`https://api.deepgram.com/v1/speak?model=${currentVoice.voice}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authorization,
        },
        body: JSON.stringify({ text: transformedText }),
      });

    let authorization = await getDeepgramAuthorizationHeader();
    let response = await requestSpeech(authorization);

    if (response.status === 401) {
      clearDeepgramAccessTokenCache();
      authorization = await getDeepgramAuthorizationHeader();
      response = await requestSpeech(authorization);
    }

    if (!response.ok) throw new Error('Failed to generate Deepgram speech');
    return response.blob();
  };

  const speakResponse = useCallback(
    async (text: string, messageId: string) => {
      if (!text) return;

      setLoadingMessageId(messageId);

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      try {
        const audioBlob = await (currentVoice.provider === 'openai'
          ? generateBackendTTS(text)
          : generateDeepgramSpeech(text));

        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
        } else {
          audioRef.current = new Audio(audioUrl);
        }

        audioRef.current.onended = () => {
          setPlayingMessageId(null);
        };

        audioRef.current.play();
        setPlayingMessageId(messageId);
        lastPlayedMessageId.current = messageId;
      } catch (error) {
        if (
          (error as Error).message.includes('Deepgram access token') ||
          (error as Error).message.includes('grant')
        ) {
          toast.error('Text-to-speech is not configured.');
        } else {
          console.error('Error generating speech:', error);
        }
      } finally {
        setLoadingMessageId(null);
      }
    },
    [currentVoice]
  );

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setPlayingMessageId(null);
    setLoadingMessageId(null);
  }, []);

  const togglePlayPause = useCallback(
    (messageId: string, text: string) => {
      if (playingMessageId === messageId) {
        if (audioRef.current) {
          audioRef.current.pause();
          setPlayingMessageId(null);
        }
      } else if (
        audioRef.current?.src &&
        messageId === lastPlayedMessageId.current
      ) {
        audioRef.current.play();
        setPlayingMessageId(messageId);
      } else {
        stopSpeaking();
        speakResponse(text, messageId);
      }
    },
    [playingMessageId, stopSpeaking, speakResponse]
  );

  return {
    playingMessageId,
    loadingMessageId,
    speakResponse,
    stopSpeaking,
    togglePlayPause,
    currentVoice,
    setCurrentVoice,
    availableVoices: TTS_VOICES,
  };
};
